import React, { useMemo, useState } from "react";
import {
  EngagementFunnelPanelProps,
  FunnelSegment,
} from "./EnagementFunnelPanel.types";
import { formatPercent } from "../../utils/formatUtils";
import { useRefreshRegister } from "../../components/RefreshProvider";
import {
  useGetPNRatio,
  usePostEmailEngagementGroupedQuery,
  usePutEmailEngagementQuery,
} from "../../app/useGeneratedApi";
import { getLastPeriod } from "../../utils/dateUtils";
import { isToday } from "date-fns";

export function useEngagementFunnelPanel(props: EngagementFunnelPanelProps) {
  const { from, to, groupTimeUnit, email = undefined } = props;
  const [tabValue, setTabValue] = useState(0);

  const activityTypes = useMemo((): (
    | "CAMPAIGN"
    | "WORKFLOW"
    | "AUTOMATION"
  )[] => {
    switch (tabValue) {
      case 1:
        return ["CAMPAIGN"];
      case 2:
        return ["WORKFLOW", "AUTOMATION"];
      case 0:
      default:
        return ["WORKFLOW", "CAMPAIGN", "AUTOMATION"];
    }
  }, [tabValue]);

  const {
    data: emailEngagementGroupedData,
    isLoading: isLoadingGroupedStatistics,
    refetch: refetchEmailEngagement,
  } = email
    ? usePutEmailEngagementQuery({
        from,
        to,
        email,
        activityTypes: activityTypes,
      })
    : usePostEmailEngagementGroupedQuery({
        from,
        to,
        groupTimeUnit,
        eventTypes: [
          "UNIQUE_CLICK",
          "UNIQUE_OPEN",
          "SEND",
          "DELIVERED",
          "UNSUBSCRIBE",
        ],
        activityTypes: activityTypes,
      });

  const {
    data: pnratio,
    isLoading: isPNRatioLoading,
    refetch: refetchPnRatio,
  } = useGetPNRatio(
    {
      from,
      to,
      activityTypes: activityTypes,
    },
    {
      enabled: !email,
    }
  );

  useRefreshRegister(async () => {
    await refetchEmailEngagement();
    await refetchPnRatio();
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const segments = React.useMemo(() => {
    const currentStats = emailEngagementGroupedData?.currentStats;

    return [
      {
        index: 0,
        value: currentStats?.sendPercent || 0,
        label: "Email Sent",
        count: currentStats?.send || 0,
        iconType: "send",
      },
      {
        index: 1,
        value: currentStats?.deliveredPercent || 0,
        label: "Email Delivered",
        count: currentStats?.delivered || 0,
        iconType: "delivered",
      },
      {
        index: 2,
        value: currentStats?.uniqueOpenPercent || 0,
        label: "Email Opened",
        count: currentStats?.uniqueOpen || 0,
        iconType: "opened",
      },
      {
        index: 3,
        value: currentStats?.uniqueClickedPercent || 0,
        label: "Email Clicked",
        count: currentStats?.uniqueClick || 0,
        iconType: "clicked",
      },
    ];
  }, [emailEngagementGroupedData]);

  const interpolatedData = React.useMemo(() => {
    function interpolatePoints(
      current: FunnelSegment,
      next?: FunnelSegment
    ): FunnelSegment[] {
      if (!next) return [current];
      const xStep = 0.25;
      const yStep = Math.abs(current.value - next.value) * 0.03;
      const yMid1 = Math.max(current.value - yStep, next.value);
      const yMid2 = next.value;
      const xMid1 = current.index + xStep;
      const xMid2 = next.index - xStep;
      return [
        current,
        { index: xMid1, value: yMid1 },
        { index: xMid2, value: yMid2 },
      ];
    }

    return segments
      .map((d, i) => {
        if (i === segments.length - 1) return [d];
        return interpolatePoints(d, segments[i + 1]);
      })
      .flat();
  }, [segments]);

  const metrics = React.useMemo(() => {
    const compareStats = emailEngagementGroupedData?.compareStats;
    const sent = formatPercent(compareStats?.compareSendPercent);
    const delivered = formatPercent(compareStats?.compareDeliveredPercent);
    const opened = formatPercent(compareStats?.compareUniqueClickedPercent);
    const clicked = formatPercent(compareStats?.compareUniqueClickedPercent);

    return {
      sent,
      delivered,
      opened,
      clicked,
    };
  }, []);

  const stageInfo = useMemo(() => {
    const stats = emailEngagementGroupedData?.currentStats;
    const compare = emailEngagementGroupedData?.compareStats;

    return segments.map((segment) => {
      const label = segment.label ?? "";
      const count = segment.count ?? 0;
      const change = (() => {
        switch (segment.iconType) {
          case "send":
            return compare?.compareSendPercent ?? 0;
          case "delivered":
            return compare?.compareDeliveredPercent ?? 0;
          case "opened":
            return compare?.compareUniqueOpenPercent ?? 0;
          case "clicked":
            return compare?.compareUniqueClickedPercent ?? 0;
          default:
            return 0;
        }
      })();

      const icon = (() => {
        switch (segment.iconType) {
          case "send":
            return "M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0M12 8v8m-4-4h8";
          case "delivered":
            return "M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0M8 12l3 3l6-6";
          case "opened":
            return "M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0";
          case "clicked":
            return "M3 3h18v18H3zM12 8v8m-4-4h8";
          default:
            return "";
        }
      })();

      return {
        label,
        value: segment.value,
        count,
        change,
        icon,
      };
    });
  }, [segments, emailEngagementGroupedData]);

  return {
    segments,
    pnratio: pnratio?.pnratio,
    isPNRatioLoading,
    interpolatedData,
    isLoading: isLoadingGroupedStatistics,
    metrics,
    handleTabChange,
    tabValue,
    stageInfo,
    lastPeriod: isToday(to || new Date())
      ? getLastPeriod(emailEngagementGroupedData?.compareDays || 0)
      : "period",
  };
}
